import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Phone, MapPin, Clock, ChevronDown, Menu, X, Shield, 
  User, LogIn, UserPlus, LayoutDashboard, Wrench, Settings, ArrowRight, LogOut 
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getAssetUrl } from '../../utils/assetHelper';
import NotificationBell from '../ui/NotificationBell';
export { getAssetUrl };

const FacebookIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const InstagramIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.13-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689-.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
);
const LinkedinIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
);

export default function Header() {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [areasDropdown, setAreasDropdown] = useState(false);
  const [accountDropdown, setAccountDropdown] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const location = useLocation();

  // Dropdown close timer refs to prevent sudden disappearance on mouse transitions
  const servicesTimeoutRef = React.useRef(null);
  const areasTimeoutRef = React.useRef(null);
  const accountTimeoutRef = React.useRef(null);
  const navContainerRef = React.useRef(null);
  const accountContainerRef = React.useRef(null);

  const clearAllTimers = () => {
    if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
    if (areasTimeoutRef.current) clearTimeout(areasTimeoutRef.current);
    if (accountTimeoutRef.current) clearTimeout(accountTimeoutRef.current);
  };

  const openServices = () => {
    clearAllTimers();
    setAreasDropdown(false);
    setAccountDropdown(false);
    setServicesDropdown(true);
  };

  const closeServices = () => {
    if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesDropdown(false);
    }, 350);
  };

  const openAreas = () => {
    clearAllTimers();
    setServicesDropdown(false);
    setAccountDropdown(false);
    setAreasDropdown(true);
  };

  const closeAreas = () => {
    if (areasTimeoutRef.current) clearTimeout(areasTimeoutRef.current);
    areasTimeoutRef.current = setTimeout(() => {
      setAreasDropdown(false);
    }, 350);
  };

  const openAccount = () => {
    clearAllTimers();
    setServicesDropdown(false);
    setAreasDropdown(false);
    setAccountDropdown(true);
  };

  const closeAccount = () => {
    if (accountTimeoutRef.current) clearTimeout(accountTimeoutRef.current);
    accountTimeoutRef.current = setTimeout(() => {
      setAccountDropdown(false);
    }, 450);
  };

  // Close dropdowns if user clicks outside both nav container and account container
  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideNav = navContainerRef.current && navContainerRef.current.contains(e.target);
      const clickedInsideAccount = accountContainerRef.current && accountContainerRef.current.contains(e.target);

      if (!clickedInsideNav) {
        setServicesDropdown(false);
        setAreasDropdown(false);
      }
      if (!clickedInsideAccount) {
        setAccountDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close all on route change
  useEffect(() => {
    setServicesDropdown(false);
    setAreasDropdown(false);
    setAccountDropdown(false);
  }, [location.pathname]);

  // Scroll listener to toggle sticky navigation reliably across browsers with zero forced reflow
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const sticky = window.scrollY > 34;
          setIsSticky(prev => (prev !== sticky ? sticky : prev));
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    const initialSticky = window.scrollY > 34;
    setIsSticky(prev => (prev !== initialSticky ? initialSticky : prev));
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Rolling Shutter Repair', desc: 'Emergency jams, slat & alignment fixes', path: '/service/rolling-shutter-repair/' },
    { name: 'Rolling Shutter Installation', desc: 'Custom commercial & industrial fitting', path: '/service/shutter-installation/' },
    { name: 'Shutter Motor Repair', desc: 'Motor rewinding, gear & remote sensor fixes', path: '/service/shutter-motor-repair/' },
    { name: 'Rolling Shutter Maintenance', desc: 'Lubrication, tensioning & safety checks', path: '/service/shutter-maintenance/' },
    { name: 'Automatic Shutter Services', desc: 'Smart remote & sensor automation', path: '/service/automatic-shutter-services/' },
    { name: 'Commercial Shutter Services', desc: 'Durable shop & retail front solutions', path: '/service/commercial-shutter-services/' },
    { name: 'Industrial Shutter Services', desc: 'Heavy-duty motorized factory shutters', path: '/service/industrial-shutter-services/' },
    { name: 'Emergency Shutter Repair', desc: '60-90 min rapid dispatch in Hyderabad', path: '/service/emergency-shutter-repair/' },
  ];

  const areas = [
    { name: 'Gachibowli', desc: 'IT Hub & Financial District', path: '/rolling-shutter-repair-in-gachibowli-premier-shutter-service/' },
    { name: 'Kondapur', desc: 'Residential & Commercial Hub', path: '/rolling-shutter-repair-in-kondapur-fast-solutions/' },
    { name: 'Madhapur', desc: 'Hitec City & Corporate Zone', path: '/rolling-shutter-repair-in-madhapur-affordable-rates/' },
    { name: 'Kukatpally', desc: 'Major Retail & Market Area', path: '/rolling-shutter-repair-in-kukatpally-trusted-service/' },
    { name: 'Miyapur', desc: 'Industrial & Metro Corridor', path: '/rolling-shutter-repair-in-miyapur-top-notch-rolling-shutter-repair-and-maintenance-in-miyapur/' },
    { name: 'Banjara Hills', desc: 'Premium Commercial Showrooms', path: '/rolling-shutter-repair-in-banjara-hills-top-quality-shutter-repair-solutions-in-banjara-hills/' },
  ];

  return (
    <>
      <header className="w-full relative z-40">
        {/* 1. Top Info Bar (not sticky - scrolls out of view naturally) */}
        <div className="bg-[#0d2137] text-gray-300 py-1 md:py-2 px-3 sm:px-4 border-b border-[#1a3a52]">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00796b] inline-block shrink-0" />
              <span className="font-medium text-white truncate text-[11px] md:text-xs">
                <span className="hidden sm:inline">Professional </span>Rolling Shutter Services<span className="hidden md:inline"> in Hyderabad</span>
              </span>
            </div>
            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0 text-gray-300 text-[11px] md:text-xs">
              <div className="hidden md:flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#00796b]" />
                <span>Hyderabad, Telangana</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Clock className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#00796b] shrink-0" />
                <span className="hidden sm:inline">Mon - Sat: </span>
                <span>9:00 AM - 7:00 PM</span>
              </div>
              <div className="hidden sm:flex items-center gap-2.5 pl-2 border-l border-gray-700">
                <a href="https://facebook.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" aria-label="Facebook">
                  <FacebookIcon />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" aria-label="Instagram">
                  <InstagramIcon />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" aria-label="LinkedIn">
                  <LinkedinIcon />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Main Navigation Bar (Clean sticky positioning that preserves document flow with zero CLS) */}
        <div 
          className="sticky top-0 z-50 w-full bg-white border-b border-gray-100 shadow-xs"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 xl:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2 lg:gap-3">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0">
              <img 
                src={getAssetUrl('assets/images/logo-v2.webp')} 
                alt="Shutter Services" 
                width="200"
                height="48"
                fetchPriority="high"
                decoding="async"
                className="h-9 sm:h-10 lg:h-9.5 xl:h-11 w-auto object-contain shrink-0"
              />
            </Link>

            {/* Desktop Navigation Links */}
            <nav ref={navContainerRef} className="hidden lg:flex items-center gap-1 xl:gap-3.5 2xl:gap-5 text-[12.5px] xl:text-[14px] font-semibold text-[#1a1a2e] shrink-0">
              <Link 
                to="/" 
                className={`whitespace-nowrap px-2 py-1 hover:text-[#00796b] transition-colors ${location.pathname === '/' ? 'text-[#00796b] border-b-2 border-[#00796b]' : ''}`}
              >
                Home
              </Link>

              {/* Multi-Column Services Dropdown */}
              <div 
                className="relative py-2 group flex items-center gap-0.5 xl:gap-1 shrink-0"
                onMouseEnter={openServices}
                onMouseLeave={closeServices}
              >
                <Link 
                  to="/our-services/" 
                  className={`whitespace-nowrap flex items-center gap-1 hover:text-[#00796b] transition-colors px-2 py-1 ${
                    (location.pathname.startsWith('/service/') || location.pathname.startsWith('/services/') || location.pathname === '/our-services' || location.pathname === '/our-services/')
                      ? 'text-[#00796b] border-b-2 border-[#00796b]' 
                      : ''
                  }`}
                  onClick={() => {
                    clearAllTimers();
                    setServicesDropdown(false);
                  }}
                >
                  <span>Services</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesDropdown ? 'rotate-180 text-[#00796b]' : ''}`} />
                </Link>

                {servicesDropdown && (
                  <div 
                    className="absolute top-full -left-12 w-[600px] pt-1.5 z-50 before:absolute before:-top-3 before:left-0 before:w-full before:h-4 before:content-['']"
                    onMouseEnter={openServices}
                    onMouseLeave={closeServices}
                  >
                    <div className="bg-white border border-gray-100 rounded-xl shadow-2xl p-4 animate-in fade-in-50 zoom-in-95 duration-150">
                      <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wider px-2 mb-2">
                        Complete Rolling Shutter Solutions
                      </div>
                      {/* 2-Columns Grid UI */}
                      <div className="grid grid-cols-2 gap-2">
                        {services.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-[#e6faf8] group/item transition-all"
                            onClick={() => {
                              clearAllTimers();
                              setServicesDropdown(false);
                            }}
                          >
                            <div className="w-8 h-8 rounded-md bg-[#e6faf8] text-[#00796b] group-hover/item:bg-[#00796b] group-hover/item:text-white flex items-center justify-center shrink-0 transition-colors mt-0.5">
                              <Wrench className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-xs font-bold text-[#0f172a] group-hover/item:text-[#00796b] transition-colors leading-tight">
                                {item.name}
                              </div>
                              <div className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                                {item.desc}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between px-2">
                        <span className="text-[11px] text-gray-500">Need emergency technician dispatch?</span>
                        <Link 
                          to="/our-services/" 
                          onClick={() => {
                            clearAllTimers();
                            setServicesDropdown(false);
                          }}
                          className="text-xs font-bold text-[#00796b] hover:text-[#00796b] flex items-center gap-1"
                        >
                          <span>View All Services</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Multi-Column Areas Dropdown */}
              {/* Multi-Column Areas Dropdown */}
              <div 
                className="relative py-2 group flex items-center gap-0.5 xl:gap-1 shrink-0"
                onMouseEnter={openAreas}
                onMouseLeave={closeAreas}
              >
                <Link 
                  to="/service-areas/" 
                  className={`whitespace-nowrap flex items-center gap-1 hover:text-[#00796b] transition-colors px-2 py-1 ${
                    (location.pathname.startsWith('/rolling-shutter-repair-in') || location.pathname === '/service-areas' || location.pathname === '/service-areas/') 
                      ? 'text-[#00796b] border-b-2 border-[#00796b]' 
                      : ''
                  }`}
                  onClick={() => {
                    clearAllTimers();
                    setAreasDropdown(false);
                  }}
                >
                  <span>Areas</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${areasDropdown ? 'rotate-180 text-[#00796b]' : ''}`} />
                </Link>

                {areasDropdown && (
                  <div 
                    className="absolute top-full -left-12 w-[520px] pt-1.5 z-50 before:absolute before:-top-3 before:left-0 before:w-full before:h-4 before:content-['']"
                    onMouseEnter={openAreas}
                    onMouseLeave={closeAreas}
                  >
                    <div className="bg-white border border-gray-100 rounded-xl shadow-2xl p-4 animate-in fade-in-50 zoom-in-95 duration-150">
                      <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wider px-2 mb-2">
                        Serving Top Localities in Hyderabad
                      </div>
                      {/* 2-Columns Grid UI */}
                      <div className="grid grid-cols-2 gap-2">
                        {areas.map((area) => (
                          <Link
                            key={area.name}
                            to={area.path}
                            className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-[#e6faf8] group/item transition-all"
                            onClick={() => {
                              clearAllTimers();
                              setAreasDropdown(false);
                            }}
                          >
                            <div className="w-8 h-8 rounded-md bg-[#e6faf8] text-[#00796b] group-hover/item:bg-[#00796b] group-hover/item:text-white flex items-center justify-center shrink-0 transition-colors mt-0.5">
                              <MapPin className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-xs font-bold text-[#0f172a] group-hover/item:text-[#00796b] transition-colors leading-tight">
                                {area.name}
                              </div>
                              <div className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                                {area.desc}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between px-2">
                        <span className="text-[11px] text-gray-500">Fast 60-90 min response across Hyderabad</span>
                        <Link
                          to="/service-areas/"
                          onClick={() => {
                            clearAllTimers();
                            setAreasDropdown(false);
                          }}
                          className="text-xs font-bold text-[#00796b] hover:text-[#00796b] flex items-center gap-1"
                        >
                          <span>View All Areas</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Link 
                to="/our-works/" 
                className={`whitespace-nowrap px-2 py-1 hover:text-[#00796b] transition-colors ${
                  location.pathname.startsWith('/our-works') || location.pathname.startsWith('/work/') || location.pathname.startsWith('/project/') || location.pathname.startsWith('/latest-projects')
                    ? 'text-[#00796b] border-b-2 border-[#00796b]' 
                    : ''
                }`}
              >
                Our Works
              </Link>
              <Link 
                to="/our-clients/" 
                className={`whitespace-nowrap px-2 py-1 hover:text-[#00796b] transition-colors ${
                  location.pathname.startsWith('/our-clients') || location.pathname.startsWith('/clients')
                    ? 'text-[#00796b] border-b-2 border-[#00796b]' 
                    : ''
                }`}
              >
                Our Clients
              </Link>
              <Link 
                to="/contact-us/" 
                className={`whitespace-nowrap px-2 py-1 hover:text-[#00796b] transition-colors ${
                  location.pathname.startsWith('/contact')
                    ? 'text-[#00796b] border-b-2 border-[#00796b]' 
                    : ''
                }`}
              >
                Contact
              </Link>
            </nav>

            {/* Right Header Actions */}
            <div className="hidden sm:flex items-center gap-1.5 lg:gap-2 xl:gap-3 shrink-0">
              {/* Call CTA */}
              <a 
                href="tel:+919603781602" 
                className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-[#00796b] transition-colors shrink-0 whitespace-nowrap p-1 xl:px-2 xl:py-1 rounded-lg hover:bg-gray-50"
                title="Call Now: +91 96037 81602"
                aria-label="Call Now: +91 96037 81602"
              >
                <div className="w-8 h-8 rounded-full bg-[#e6faf8] text-[#00796b] flex items-center justify-center shrink-0 border border-teal-100 shadow-xs hover:bg-[#00796b] hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                </div>
                <div className="hidden xl:block text-left shrink-0 whitespace-nowrap">
                  <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Call Now</div>
                  <div className="font-extrabold text-[#0d2137] text-xs xl:text-sm whitespace-nowrap leading-tight">+91 96037 81602</div>
                </div>
              </a>

              {/* Notification Bell (Logged-in users) */}
              {user && <NotificationBell align="right" />}

              {/* USER ACCOUNT BUTTON & DROPDOWN */}
              <div 
                ref={accountContainerRef}
                className="relative py-1 group shrink-0"
                onMouseEnter={openAccount}
                onMouseLeave={closeAccount}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (accountDropdown) {
                      setAccountDropdown(false);
                    } else {
                      openAccount();
                    }
                  }}
                  className="bg-[#00796b] hover:bg-[#004d40] text-white text-xs font-bold p-2 xl:px-3.5 xl:py-2.5 rounded-full xl:rounded-lg flex items-center gap-1.5 xl:gap-2 transition-all shadow-xs cursor-pointer active:scale-95 focus:outline-none shrink-0 whitespace-nowrap"
                  aria-label={user ? `My Account: ${user.name || user.email}` : "My Account"}
                  title={user ? (user.name || user.email) : "My Account"}
                >
                  <User className="w-4 h-4 text-white shrink-0" />
                  <span className="hidden xl:inline max-w-[100px] xl:max-w-[120px] truncate">{user ? (user.name || user.email) : 'My Account'}</span>
                  <ChevronDown className={`hidden xl:inline w-3.5 h-3.5 transition-transform duration-200 shrink-0 ${accountDropdown ? 'rotate-180' : ''}`} />
                </button>

                {accountDropdown && (
                  <div 
                    className="absolute right-0 top-full w-60 pt-1.5 z-50 before:absolute before:-top-3 before:left-0 before:w-full before:h-4 before:content-['']"
                    onMouseEnter={openAccount}
                    onMouseLeave={closeAccount}
                  >
                    <div className="bg-white border border-gray-100 rounded-xl shadow-2xl p-2 animate-in fade-in-50 zoom-in-95 duration-150">
                      {user ? (
                        <>
                          <div className="px-3 py-2 border-b border-gray-100 mb-1">
                            <div className="text-xs font-bold text-[#0f172a] truncate">{user.name || 'User'}</div>
                            <div className="text-[11px] text-gray-500 truncate">{user.email}</div>
                            <div className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-teal-50 text-[#00796b]">
                              {user.role}
                            </div>
                          </div>

                          <div className="space-y-0.5">
                            <Link
                              to={
                                ['admin', 'super_admin', 'staff'].includes(user.role) 
                                  ? '/admin' 
                                  : user.role === 'technician' 
                                    ? '/technician' 
                                    : '/client'
                              }
                              onClick={() => {
                                clearAllTimers();
                                setAccountDropdown(false);
                              }}
                              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-[#e6faf8] hover:text-[#00796b] transition-colors"
                            >
                              <LayoutDashboard className="w-4 h-4 text-[#00796b]" />
                              <span>
                                {['admin', 'super_admin', 'staff'].includes(user.role) 
                                  ? 'Admin Operations' 
                                  : user.role === 'technician' 
                                    ? 'Technician App' 
                                    : 'My Bookings & Profile'}
                              </span>
                            </Link>

                            <button
                              type="button"
                              onClick={async () => {
                                clearAllTimers();
                                setAccountDropdown(false);
                                await logout();
                              }}
                              className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                            >
                              <LogOut className="w-4 h-4 text-red-600" />
                              <span>Log Out</span>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="px-3 py-2 border-b border-gray-100 mb-1">
                            <div className="text-xs font-bold text-[#0f172a]">Customer Portal</div>
                            <div className="text-[11px] text-gray-500">Manage bookings & track service</div>
                          </div>

                          <div className="space-y-0.5">
                            <Link
                              to="/customer/login"
                              onClick={() => {
                                clearAllTimers();
                                setAccountDropdown(false);
                              }}
                              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-[#e6faf8] hover:text-[#00796b] transition-colors"
                            >
                              <LogIn className="w-4 h-4 text-[#00796b]" />
                              <span>Login</span>
                            </Link>

                            <Link
                              to="/customer/register"
                              onClick={() => {
                                clearAllTimers();
                                setAccountDropdown(false);
                              }}
                              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-[#e6faf8] hover:text-[#00796b] transition-colors"
                            >
                              <UserPlus className="w-4 h-4 text-[#00796b]" />
                              <span>Register</span>
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Hamburger Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-[#00796b]"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Fixed Spacer to prevent content jump when navbar becomes fixed */}
        {isSticky && <div className="h-[68px] sm:h-[72px]" />}

        {/* Mobile Drawer Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-4 space-y-3 shadow-lg">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-semibold text-gray-800 hover:text-[#00796b]">Home</Link>
            
            {/* Mobile Services Section */}
            <div className="pt-1 pb-1 border-t border-gray-100">
              <div className="flex items-center justify-between py-1">
                <Link 
                  to="/our-services/" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs font-bold text-gray-800 hover:text-[#00796b] uppercase tracking-wider"
                >
                  Our Services
                </Link>
                <Link 
                  to="/our-services/" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[11px] font-bold text-[#00796b] hover:underline"
                >
                  View All &rarr;
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-1 mt-1 pl-2">
                {services.map((s) => (
                  <Link key={s.name} to={s.path} onClick={() => setMobileMenuOpen(false)} className="py-1 text-xs text-gray-600 hover:text-[#00796b]">
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile Areas Section */}
            <div className="pt-1 pb-1 border-t border-gray-100">
              <div className="flex items-center justify-between py-1">
                <Link 
                  to="/service-areas/" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs font-bold text-gray-800 hover:text-[#00796b] uppercase tracking-wider"
                >
                  Service Areas
                </Link>
                <Link 
                  to="/service-areas/" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[11px] font-bold text-[#00796b] hover:underline"
                >
                  View All 600+ &rarr;
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-1 mt-1 pl-2">
                {areas.map((a) => (
                  <Link key={a.name} to={a.path} onClick={() => setMobileMenuOpen(false)} className="py-1 text-xs text-gray-600 hover:text-[#00796b]">
                    {a.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/our-works/" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-semibold text-gray-800 hover:text-[#00796b]">Our Works</Link>
            <Link to="/our-clients/" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-semibold text-gray-800 hover:text-[#00796b]">Our Clients</Link>
            <Link to="/contact-us/" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-semibold text-gray-800 hover:text-[#00796b]">Contact</Link>
            
            {/* Mobile User Account & Call Buttons */}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              {user ? (
                <div className="grid grid-cols-2 gap-2">
                  <Link 
                    to={
                      ['admin', 'super_admin', 'staff'].includes(user.role) 
                        ? '/admin' 
                        : user.role === 'technician' 
                          ? '/technician' 
                          : '/client'
                    }
                    onClick={() => setMobileMenuOpen(false)} 
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#00796b] text-white text-xs font-bold hover:bg-[#00796b]"
                  >
                    <LayoutDashboard className="w-3.5 h-3.5" />
                    <span>My Dashboard</span>
                  </Link>
                  <button 
                    type="button"
                    onClick={async () => {
                      setMobileMenuOpen(false);
                      await logout();
                    }} 
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 cursor-pointer"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link 
                    to="/customer/login" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-[#00796b] text-[#00796b] text-xs font-bold hover:bg-[#e6faf8]"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    <span>Login</span>
                  </Link>
                  <Link 
                    to="/customer/register" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#00796b] text-white text-xs font-bold hover:bg-[#00796b]"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>Register</span>
                  </Link>
                </div>
              )}

              <a 
                href="tel:+919603781602" 
                className="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#0d2137] text-white text-xs font-bold"
              >
                <Phone className="w-3.5 h-3.5" />
                <span>Call +91 96037 81602</span>
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
